// App.js
import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { trackPageView } from './analytics';
import AuthForm from './components/AuthForm';
import GuildForm from './components/GuildForm';
import GuildBattleList from './components/GuildBattleList';
import GuildWarCount from './components/GuildWarCount';
import GuildWarNihon from './components/GuildWarNihon';
import GuildWarCma from './components/GuildWarCma';
import DummyGuildWarCMA from './components/DummyGuildWarCma';
import DummyGuildWarNihon from './components/DummyGuildWarNihon';
import GuildInfo from './components/GuildInfo';
import DreamTeam from './components/DreamTeam';
import GuildWarGuardian from './components/GuildWarGuardian';
import GuildInfoCma from './components/GuildInfoCma';
import GuildInfoNihon from './components/GuildInfoNihon';
import GuildInfoDreamTeam from './components/GuildInfoDreamTeam';
import GuildWarInstructionsNihon from './components/GuildWarInstructionsNihon';
import GuildWarStatus from './components/GuildWarCMAStatus';
import AvailableTowers from './components/AvailableTowers';
import GuildWarRankingNihon from './components/GuildWarRankingNihon';
import GuildWarRankingCMA from './components/GuildWarRankingCMA';
import GuildWarRanking from './components/GuildWarRanking';
import InternalLinks from './components/InternalLinks';
import GuildWarCaN from './components/GuildWarCaN';
import AccessLogDashboard from './components/AccessLogDashboard';
import ClosedNotice from './components/ClosedNotice';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(''); // roleとしてviewerやadminを管理
  const [submitError, setSubmitError] = useState('');
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [firstRoundResults, setFirstRoundResults] = useState([]);
  const [secondRoundResults, setSecondRoundResults] = useState([]);
  const [guildNames, setGuildNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  // ページビューのトラッキング
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  const fetchGuildNames = async () => {
    try {
      const response = await fetch(
        'https://rangers-api-3mfw.onrender.com/api/guilds/guild-names'
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      // 新しいデータ構造に対応（すでにJSONオブジェクトとして返される）
      setGuildNames(data);
    } catch (error) {
      console.error('Failed to fetch guild names:', error);
    }
  };

  const fetchBattleResults = async () => {
    try {
      setIsLoading(true);

      let response = await fetch(
        'https://rangers-api-3mfw.onrender.com/api/guilds/guild-battles/first-round'
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      let data = await response.json();
      setFirstRoundResults(data);

      response = await fetch(
        'https://rangers-api-3mfw.onrender.com/api/guilds/guild-battles/second-round'
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      data = await response.json();
      setSecondRoundResults(data);

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch battle results:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchBattleResults();
      fetchGuildNames();
    }
  }, [isAuthenticated]);

  const handleAuthSuccess = (authCode) => {
    setIsAuthenticated(true);
    setRole(authCode); // roleを設定
  };

  const handleGuildSubmit = async (guildName, opponentGuildName, battleDate) => {
    if (window.confirm('Do you want to register the battle information?')) {
      try {
        // ギルド名から完全なギルド情報を取得
        const guild = guildNames.find(g => g.name === guildName);
        const opponentGuild = guildNames.find(g => g.name === opponentGuildName);

        if (!guild || !opponentGuild) {
          throw new Error('Guild information not found');
        }

        const response = await fetch(
          'https://rangers-api-3mfw.onrender.com/api/guilds/guild-battles',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              guildName,
              opponentGuildName,
              battleDate,
              guildRank: guild.lastGuildWarRank,
              opponentGuildRank: opponentGuild.lastGuildWarRank,
              guildId: guild.gid,
              opponentGuildId: opponentGuild.gid
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        setLastSubmitted({
          guildName,
          opponentGuildName,
          battleDate,
          guildRank: guild.lastGuildWarRank,
          opponentGuildRank: opponentGuild.lastGuildWarRank
        });
        setSubmitError('');
        await fetchBattleResults();
      } catch (error) {
        console.error('Error submitting guild battle:', error);
        setSubmitError('Error occurred.');
        setLastSubmitted(null);
      }
    }
  };

  const handleNewGuildNameRegistration = async () => {
    if (window.confirm('Do you want to register the guild name and rank information?')) {
      try {
        // サーバーからギルド名リストを取得
        const response = await fetch('https://rangers-api-3mfw.onrender.com/api/guilds/get-guild-names');
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // ランク1～50のギルド情報を抽出（名前とランクを含む）
        const validGuilds = data
          .filter(guild => guild.lastGuildWarRank >= 1 && guild.lastGuildWarRank <= 50)
          .map(guild => ({
            name: guild.name,
            lastGuildWarRank: guild.lastGuildWarRank,
            gid: guild.gid
          }));

        // ギルド情報をサーバーに登録
        const postResponse = await fetch(
          'https://rangers-api-3mfw.onrender.com/api/guilds/register-guild-names',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ guilds: validGuilds }), // guildNamesからguildsに変更
          }
        );

        if (!postResponse.ok) {
          throw new Error(`HTTP error! Status: ${postResponse.status}`);
        }

        await fetchGuildNames(); // ギルド名リストを更新
        window.confirm('Guild name and rank information registration completed');
      } catch (error) {
        console.error('Failed to register guild names:', error);
        window.confirm('Registration failed');
      }
    }
  };

  const handleDeleteGuildNames = async () => {
    if (window.confirm('Do you want to delete all registered guild names?')) {
      try {
        const response = await fetch(
          'https://rangers-api-3mfw.onrender.com/api/guilds/delete-guild-names',
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        if (response.ok) {
          window.confirm('Deletion completed');
        }
      } catch (error) {
        console.error('Failed to delete guild names:', error);
      }
    }
  };

  const handleReload = () => {
    fetchBattleResults();
    fetchGuildNames();
  };

  return (
    <>
      <Routes>
        <Route
          path="/guild-war-predict"
          element={
            <div className="App">
              <h1>GuildWar Predict</h1>
              {!isAuthenticated ? (
                <AuthForm onAuthSuccess={handleAuthSuccess} />
              ) : (
                <>
                  {isLoading ? (
                    <p>Loading data...(It may take about 1 minute)</p>
                  ) : (
                    <>
                      {role !== 'viewer' && (
                        <div style={{ marginBottom: '40px' }}>
                          <GuildForm
                            onGuildSubmit={handleGuildSubmit}
                            guildNames={guildNames} // 完全なギルド情報を渡す
                          />
                        </div>
                      )}
                      {submitError && <p className="error">{submitError}</p>}
                      {lastSubmitted && (
                        <p>
                          <strong>{lastSubmitted.battleDate}</strong>の
                          <strong style={{ color: 'green' }}>
                            {lastSubmitted.guildName}
                          </strong>
                          's opponent is
                          <strong style={{ color: 'green' }}>
                            {lastSubmitted.opponentGuildName}
                          </strong>
                          registered.
                        </p>
                      )}
                      <div style={{ margin: '20px 0' }}>
                        <button onClick={handleReload} className="reload-button">
                          ⟳ Reload
                        </button>
                      </div>
                      <GuildBattleList
                        firstRoundResults={firstRoundResults}
                        secondRoundResults={secondRoundResults}
                        allGuilds={guildNames} // ギルド情報全体を渡す
                      />
                      {role === 'admin' && (
                        <>
                          <br />
                          <div className="admin-controls">
                            <button onClick={handleNewGuildNameRegistration}>
                              Register Guild Name and Rank
                            </button>
                            <button onClick={handleDeleteGuildNames} className="danger">
                              Delete All Guild Names
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          }
        />
        <Route path="/guildWarNihon" element={<DummyGuildWarNihon />} />
        <Route path="/guildWarCMA" element={<DummyGuildWarCMA />} />
        <Route path="/guildWarCMAProd" element={<GuildWarCma />} />
        <Route path="/guildWarNihonProd" element={<GuildWarNihon />} />
        <Route path="/guildWarCaN" element={<GuildWarCaN />} />
        <Route path="/guildWarCount" element={<GuildWarCount />} />
        <Route path="/dreamTeam" element={<DreamTeam />} />
        <Route path="/guildInfo" element={<GuildInfo />} />
        <Route path="/guildwarGuardian" element={<GuildWarGuardian />} />
        <Route path="/guildInfoCma" element={<GuildInfoCma />} />
        <Route path="/guildInfoNihon" element={<GuildInfoNihon />} />
        <Route path="/guildInfoDreamTeam" element={<GuildInfoDreamTeam />} />
        <Route path="/guildWarInstructionsNihon" element={<GuildWarInstructionsNihon />} />
        <Route path="/guild-war-status-cma" element={<GuildWarStatus />} />
        <Route path="/available-towers" element={<AvailableTowers />} />
        <Route path="/guild-war-ranking-nihon" element={<GuildWarRankingNihon />} />
        <Route path="/guild-war-ranking-cma" element={<GuildWarRankingCMA />} />
        <Route path="/guild-war-ranking" element={<GuildWarRanking />} />
        <Route path="/access-logs" element={<AccessLogDashboard />} />
        <Route path="/links" element={<InternalLinks />} />
        <Route path="*" element={<ClosedNotice />} />
      </Routes>
    </>
  );
};

export default App;
